import { useEffect, type RefObject } from 'react';

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useNewLandingEffects(
  rootRef: RefObject<HTMLElement | null>,
  onOpenScoring: () => void
): void {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const reduce = prefersReducedMotion();
    const glow = 1;
    const cleanup: Array<() => void> = [];

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-open-scoring]')) {
        e.preventDefault();
        onOpenScoring();
      }
    };
    root.addEventListener('click', onClick);
    cleanup.push(() => root.removeEventListener('click', onClick));

    const reveals = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'));
    if (!reduce) {
      reveals.forEach((el) => {
        el.style.opacity = '0';
        el.style.transform = 'translateY(46px)';
        const d = parseInt(el.getAttribute('data-delay') || '0', 10);
        el.style.transition = `opacity .95s cubic-bezier(.16,1,.3,1) ${d}ms, transform .95s cubic-bezier(.16,1,.3,1) ${d}ms`;
      });
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const el = entry.target as HTMLElement;
              el.style.opacity = '1';
              el.style.transform = 'none';
              io.unobserve(el);
            }
          });
        },
        { threshold: 0.18 }
      );
      reveals.forEach((el) => io.observe(el));
      cleanup.push(() => io.disconnect());
    }

    const bar = root.querySelector<HTMLElement>('[data-progress]');
    const ghosts = Array.from(root.querySelectorAll<HTMLElement>('[data-ghost]'));
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      if (bar) bar.style.transform = `scaleX(${max > 0 ? h.scrollTop / max : 0})`;
      ghosts.forEach((g, i) => {
        const r = g.getBoundingClientRect();
        const p = (window.innerHeight - r.top) / (window.innerHeight + r.height);
        g.style.transform = `translateY(${(p - 0.5) * (i % 2 ? -90 : 90)}px)`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    cleanup.push(() => window.removeEventListener('scroll', onScroll));

    const phone = root.querySelector<HTMLElement>('[data-phone]');
    const stage = root.querySelector<HTMLElement>('[data-phone-stage]');
    if (phone && stage && !reduce) {
      let tx = -16;
      let ty = 8;
      let cx = -16;
      let cy = 8;
      const onMove = (e: PointerEvent) => {
        const r = stage.getBoundingClientRect();
        const nx = (e.clientX - r.left) / r.width - 0.5;
        const ny = (e.clientY - r.top) / r.height - 0.5;
        tx = -16 + nx * -22;
        ty = 8 + ny * 16;
      };
      const onLeave = () => {
        tx = -16;
        ty = 8;
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerleave', onLeave);
      let raf = 0;
      const loop = () => {
        cx += (tx - cx) * 0.06;
        cy += (ty - cy) * 0.06;
        phone.style.transform = `rotateY(${cx}deg) rotateX(${cy}deg)`;
        raf = requestAnimationFrame(loop);
      };
      loop();
      cleanup.push(() => {
        cancelAnimationFrame(raf);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerleave', onLeave);
      });
    } else if (phone) {
      phone.style.transform = 'rotateY(-16deg) rotateX(8deg)';
    }

    root.querySelectorAll<HTMLElement>('[data-tilt]').forEach((card) => {
      if (reduce) return;
      card.style.transition = card.style.transition
        ? `${card.style.transition}, box-shadow .4s`
        : 'box-shadow .4s';
      const move = (e: PointerEvent) => {
        const r = card.getBoundingClientRect();
        const nx = (e.clientX - r.left) / r.width - 0.5;
        const ny = (e.clientY - r.top) / r.height - 0.5;
        card.style.transform = `perspective(900px) rotateY(${nx * 5}deg) rotateX(${ny * -5}deg) translateY(-4px)`;
        card.style.boxShadow = '0 30px 60px rgba(0,0,0,.5)';
      };
      const leave = () => {
        card.style.transform = 'none';
        card.style.boxShadow = 'none';
      };
      card.addEventListener('pointermove', move);
      card.addEventListener('pointerleave', leave);
      cleanup.push(() => {
        card.removeEventListener('pointermove', move);
        card.removeEventListener('pointerleave', leave);
      });
    });

    root.querySelectorAll<HTMLElement>('[data-count]').forEach((el) => {
      const target = parseInt(el.getAttribute('data-count') || '0', 10);
      if (reduce) {
        el.textContent = String(target);
        return;
      }
      const t0 = performance.now();
      const tick = (now: number) => {
        const p = Math.min(1, (now - t0) / 1800);
        el.textContent = String(Math.round(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) requestAnimationFrame(tick);
      };
      const timeout = window.setTimeout(() => requestAnimationFrame(tick), 900);
      cleanup.push(() => clearTimeout(timeout));
    });

    const panels = Array.from(root.querySelectorAll<HTMLElement>('[data-screen-panel]'));
    const sdots = Array.from(root.querySelectorAll<HTMLElement>('[data-screen-dot]'));
    const snames = [
      'CLASSIC DASHBOARD',
      'LINEUP',
      'TRANSFER MARKET',
      'DRAFT DASHBOARD',
      'LIVE DRAFT',
    ];
    const sname = root.querySelector<HTMLElement>('[data-screen-name]');
    if (panels.length) {
      let si = 0;
      let siv: number | null = null;
      const show = (i: number) => {
        panels.forEach((p, j) => {
          p.style.opacity = j === i ? '1' : '0';
          p.style.transform = j === i ? 'none' : 'scale(1.02)';
          p.style.pointerEvents = j === i ? 'auto' : 'none';
        });
        sdots.forEach((d, j) => {
          d.style.background = j === i ? '#7fe09c' : 'rgba(255,255,255,.18)';
          d.style.width = j === i ? '22px' : '7px';
        });
        if (sname) sname.textContent = snames[i] || '';
      };
      show(0);
      const start = () => {
        siv = window.setInterval(() => {
          si = (si + 1) % panels.length;
          show(si);
        }, 4500);
      };
      if (!reduce) start();
      const onDot = sdots.map((d, j) => {
        const handler = () => {
          si = j;
          show(j);
          if (siv) {
            clearInterval(siv);
            start();
          }
        };
        d.addEventListener('click', handler);
        return () => d.removeEventListener('click', handler);
      });
      cleanup.push(() => {
        if (siv) clearInterval(siv);
        onDot.forEach((fn) => fn());
      });
    }

    const rv = root.querySelector<HTMLElement>('[data-rulevalue]');
    if (rv) {
      const vals = ['+4', '+7', '+10'];
      let vi = 0;
      const iv = window.setInterval(() => {
        vi = (vi + 1) % vals.length;
        rv.style.transform = 'scale(1.25)';
        rv.textContent = vals[vi];
        rv.style.transition = 'transform .25s cubic-bezier(.16,1,.3,1)';
        window.setTimeout(() => {
          rv.style.transform = 'scale(1)';
        }, 200);
      }, 2200);
      cleanup.push(() => clearInterval(iv));
    }

    const clocks = Array.from(root.querySelectorAll<HTMLElement>('[data-clock]'));
    if (clocks.length) {
      let s = 24;
      const iv = window.setInterval(() => {
        s = s <= 1 ? 30 : s - 1;
        clocks.forEach((c) => {
          c.textContent = `0:${String(s).padStart(2, '0')}`;
        });
      }, 1000);
      cleanup.push(() => clearInterval(iv));
    }

    const canvas = root.querySelector<HTMLCanvasElement>('[data-bg-canvas]');
    if (canvas && !reduce) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        let w = 0;
        let h = 0;
        let dpr = 1;
        const resize = () => {
          dpr = Math.min(2, window.devicePixelRatio || 1);
          w = canvas.clientWidth;
          h = canvas.clientHeight;
          canvas.width = w * dpr;
          canvas.height = h * dpr;
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        resize();
        window.addEventListener('resize', resize);
        const particles = Array.from({ length: 60 }, () => ({
          x: Math.random(),
          y: Math.random(),
          r: 0.6 + Math.random() * 1.6,
          v: 0.02 + Math.random() * 0.06,
          o: 0.15 + Math.random() * 0.5,
        }));
        let raf2 = 0;
        const draw = () => {
          ctx.clearRect(0, 0, w, h);
          const sc = document.documentElement.scrollTop;
          ctx.strokeStyle = 'rgba(255,255,255,.045)';
          ctx.lineWidth = 1.5;
          const circles = [
            { x: w * 0.85, y: 500 - sc * 0.12, r: 260 },
            { x: w * 0.06, y: 1500 - sc * 0.18, r: 300 },
            { x: w * 0.5, y: 2600 - sc * 0.25, r: 380 },
            { x: w * 0.92, y: 4200 - sc * 0.3, r: 320 },
            { x: w * 0.1, y: 6000 - sc * 0.35, r: 360 },
          ];
          circles.forEach((c) => {
            const cy = (((c.y % (h + 900)) + (h + 900)) % (h + 900)) - 450;
            ctx.beginPath();
            ctx.arc(c.x, cy, c.r, 0, Math.PI * 2);
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(c.x, cy, 4, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(255,255,255,.05)';
            ctx.fill();
          });
          particles.forEach((p) => {
            p.y -= p.v / h;
            if (p.y < -0.02) {
              p.y = 1.02;
              p.x = Math.random();
            }
            ctx.beginPath();
            ctx.arc(p.x * w, p.y * h, p.r, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(127,224,156,${p.o * 0.35 * glow})`;
            ctx.fill();
          });
          raf2 = requestAnimationFrame(draw);
        };
        draw();
        cleanup.push(() => {
          cancelAnimationFrame(raf2);
          window.removeEventListener('resize', resize);
        });
      }
    }

    return () => cleanup.forEach((fn) => fn());
  }, [rootRef, onOpenScoring]);
}
